import { z } from 'zod';
// ── Badges ──────────────────────────────────────────────────────────────────
export const BadgeTriggerTypeSchema = z.enum([
    'session_count',
    'kwh_delivered',
    'streak_days',
    'spending_total', // in cents
    'referral_count',
    'night_sessions', // sessions started between 22:00 and 05:00
    'early_sessions', // sessions started between 05:00 and 08:00
    'weekend_sessions', // sessions started on Saturday or Sunday
    'monthly_sessions', // max sessions in any single calendar month
    'marathon_session', // single session >= threshold minutes
    'speed_demon', // count of sessions under 15 min
    'locations_visited', // distinct location count
    'co2_offset_kg', // estimated CO2 avoided in kg (threshold in kg)
    'hosted_sessions', // guest sessions completed on a charger this user owns (Phase 8)
    'peer_charging', // guest sessions this user completed on someone else's charger (Phase 8)
    'special_event', // manually awarded
]);
export const BadgeRaritySchema = z.enum(['common', 'rare', 'epic', 'legendary']);
/** Points awarded when a user earns this badge */
export const RARITY_POINTS = {
    common: 25,
    rare: 100,
    epic: 250,
    legendary: 1000,
};
export const BadgeDefinitionSchema = z.object({
    _id: z.string(),
    /** Stable string ID (e.g. 'first_charge') used for awarding. */
    badge_id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().min(1),
    icon_url: z.string().optional(),
    trigger_type: BadgeTriggerTypeSchema,
    /** Threshold to unlock (e.g. 100 for "100 sessions") */
    threshold: z.number().nonnegative(),
    rarity: BadgeRaritySchema,
    is_active: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
export const UserBadgeSchema = z.object({
    _id: z.string(),
    user_id: z.string(),
    badge_id: z.string(), // matches BadgeDefinition.badge_id
    earned_at: z.coerce.date(),
    notified: z.boolean(),
});
// ── Ranks ───────────────────────────────────────────────────────────────────
export const RankTierSchema = z.enum(['bronze', 'silver', 'gold', 'platinum', 'diamond']);
export const RankSchema = z.object({
    _id: z.string(),
    name: z.string().min(1),
    tier: RankTierSchema,
    /** Minimum points to reach this rank */
    min_points: z.number().int().nonnegative(),
    /** Optional minimum kWh delivered */
    min_kwh: z.number().nonnegative().default(0),
    /** Optional minimum session count */
    min_sessions: z.number().int().nonnegative().default(0),
    perks: z.array(z.string()),
    /** Skin IDs unlocked at this rank */
    skin_unlocks: z.array(z.string()),
    display_order: z.number().int().nonnegative(),
});
// ── Skins ───────────────────────────────────────────────────────────────────
export const SkinUnlockTypeSchema = z.enum(['rank_required', 'badge_required', 'special']);
export const SkinSchema = z.object({
    _id: z.string(),
    /** Stable string ID */
    skin_id: z.string().min(1),
    name: z.string().min(1),
    preview_url: z.string().optional(),
    unlock_type: SkinUnlockTypeSchema,
    /** For rank_required: tier name; for badge_required: badge_id; for special: free-form */
    unlock_value: z.string(),
    is_active: z.boolean(),
});
// ── Skin Unlock metadata (dual-mode overlay) ───────────────────────────────
//
// Shape of the `metadata` field on an `Unlock` doc with category === 'skin'.
// Before this refactor the metadata locked the driver into a single mode
// (e.g. Midnight = dark only). Now each skin carries BOTH a `light` and a
// `dark` variant; the driver's own mode toggle picks which variant applies.
//
// `themeId` still composes the skin over a base theme family (Obsidian
// layers over Luce; the others layer over Default). It does NOT force the
// mode.
//
// Variant fields beyond accent/charging are optional — most skins only
// override the two accent channels. `surface` and `bg` exist for skins
// whose light/dark identity requires shifting the page background (e.g.
// Midnight-light's pale icy blue, Neon-light's soft pink).
export const SkinVariantOverrideSchema = z.object({
    accentColor: z.string(),
    chargingColor: z.string(),
    /** Optional --evi-surface override. Leave unset to inherit the base theme's surface. */
    surface: z.string().optional(),
    /** Optional --evi-bg override. Leave unset to inherit the base theme's bg. */
    bg: z.string().optional(),
    /**
     * Optional --evi-border override. Needed by skins whose surface tone is a
     * whole step away from the base family (e.g. Bloom's pastel peach), so
     * card borders stay tonally matched instead of inheriting the base
     * family's crisper border calibration.
     */
    border: z.string().optional(),
    /**
     * Optional --evi-text-mute override. Same reason as `border` — skins
     * with a distinctive surface tone may need a muted text colour that
     * actually reads against that surface.
     */
    textMute: z.string().optional(),
});
export const SkinUnlockMetadataSchema = z.object({
    /**
     * Base theme family the skin composes over. Originally just
     * 'default' + 'luce' (the only two family registry entries
     * pre-Driver-Themes); extended with the post-Driver-Themes
     * families (neon / electric-light / dark-electric / obsidian /
     * cartographic / aurora) so skins seeded against the new
     * families don't fail Zod validation. Must stay in lockstep
     * with the ThemeFamilyId union in
     * packages/skins/src/contract.ts.
     */
    themeId: z.enum([
        'default', 'luce', 'cartographic',
        'neon', 'electric-light', 'dark-electric', 'obsidian',
        'aurora',
        'cyber',
    ]),
    /** Optional rank gate — minimum tier required to purchase. */
    tierMinimum: z.string().optional(),
    variants: z.object({
        light: SkinVariantOverrideSchema,
        dark: SkinVariantOverrideSchema,
    }),
});
// ── Avatars ─────────────────────────────────────────────────────────────────
export const AvatarStyleSchema = z.enum([
    'adventurer-neutral',
    'avataaars-neutral',
    'big-ears',
    'big-ears-neutral',
    'big-smile',
    'bottts-neutral',
    'lorelei-neutral',
]);
/** Ordered avatar-style list — the SINGLE SOURCE OF TRUTH. The DB models (User,
 *  LeaderboardSnapshot), the API route body schema, and the web avatar picker
 *  all reference this instead of re-listing styles. Neutral sets lead; the
 *  picker defaults to the first. DiceBear 7.x style ids. */
export const AVATAR_STYLES = AvatarStyleSchema.options;
/** Superset accepted at the STORAGE layer only. The picker and the write API
 *  (POST /gamification/avatar) offer AVATAR_STYLES (the 7 above), but the
 *  Mongoose enums must ALSO keep accepting the legacy ids that pre-existing
 *  users still carry — because Mongoose validates the WHOLE document on save
 *  (not just modified paths), so an unrelated user.save() (login sets
 *  last_login), or copying a stored value into a LeaderboardSnapshot, would
 *  otherwise throw a ValidationError on a grandfathered avatar. Never break
 *  saved avatars: legacy values save + render (buildAvatarUrl) untouched; only
 *  the picker stops offering them and a re-generate moves onto the new set. */
export const AVATAR_STYLES_STORED = [
    ...AVATAR_STYLES,
    'avataaars',
    'bottts',
    'pixel-art',
    'adventurer',
];
/** Build a DiceBear avatar URL — no storage needed, deterministic from style+seed */
export function buildAvatarUrl(style, seed) {
    const safeSeed = encodeURIComponent(seed);
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${safeSeed}`;
}
// ── Leaderboard ─────────────────────────────────────────────────────────────
export const LeaderboardScopeSchema = z.enum(['global', 'tenant', 'regional']);
export const LeaderboardEntrySchema = z.object({
    user_id: z.string(),
    username: z.string().optional(),
    avatar_style: AvatarStyleSchema.optional(),
    avatar_seed: z.string().optional(),
    current_rank: z.string().optional(),
    points: z.number().int().nonnegative(),
    total_kwh: z.number().nonnegative(),
    total_sessions: z.number().int().nonnegative(),
    position: z.number().int().positive(),
});
export const LeaderboardSnapshotSchema = z.object({
    _id: z.string(),
    scope: LeaderboardScopeSchema,
    /** For tenant scope: the tenant_id; for regional: a region identifier; for global: null */
    scope_id: z.string().optional(),
    entries: z.array(LeaderboardEntrySchema),
    generated_at: z.coerce.date(),
});
// ── User profile (gamification subset) ──────────────────────────────────────
export const GamificationProfileSchema = z.object({
    user_id: z.string(),
    username: z.string().optional(),
    avatar_style: AvatarStyleSchema.optional(),
    avatar_seed: z.string().optional(),
    avatar_url: z.string().optional(),
    equipped_skin: z.string().optional(),
    points: z.number().int().nonnegative(),
    total_kwh: z.number().nonnegative(),
    total_sessions: z.number().int().nonnegative(),
    current_rank: z.string().optional(),
    next_rank: z.string().optional(),
    points_to_next_rank: z.number().int().nonnegative().optional(),
    badges_earned: z.number().int().nonnegative(),
    leaderboard_position: z.number().int().positive().optional(),
});
// ── Username ────────────────────────────────────────────────────────────────
export const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;
// ── Maintenance windows (chargers) ──────────────────────────────────────────
export const MaintenanceWindowSchema = z.object({
    _id: z.string().optional(),
    start: z.coerce.date(),
    end: z.coerce.date(),
    reason: z.string().min(1).max(500),
    created_by: z.string().optional(),
    created_at: z.coerce.date().optional(),
});
export const CreateMaintenanceWindowSchema = z.object({
    start: z.coerce.date(),
    end: z.coerce.date(),
    reason: z.string().min(1).max(500),
});
// ── User-summary payload (GET /gamification/user-summary) ─────────────────
//
// A single read feeding the RewardsPage (hero + rank progress + badge
// grid + history timeline) and Profile points card. Built server-side
// so the client stays format-free.
//
// Field names are camelCased because they're direct consumers of a
// public HTTP response — matches the pattern used by
// OperatorDashboardData / FavoriteStation shipped earlier.
export const BadgePublicTierSchema = z.enum(['bronze', 'silver', 'gold', 'platinum', 'diamond']);
export const BadgePublicCategorySchema = z.enum([
    'charging', 'social', 'loyalty', 'exploration', 'milestone',
]);
export const BadgePublicSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string(),
    /** Legacy icon key (e.g. "zap", "battery"). Kept for back-compat
     *  with clients that haven't switched to iconName yet. The
     *  Lucide-driven BadgeIcon prefers iconName + colorTier. */
    icon: z.string(),
    /** Lucide-react component name (PascalCase). When set, PWA
     *  renders via dynamic Lucide lookup; falls back to 'Award' on
     *  miss. */
    iconName: z.string().optional(),
    /** Color-tier index 1-4 driving badge circle saturation.
     *  Optional — derived from `tier` when absent. */
    colorTier: z.number().int().min(1).max(4).optional(),
    tier: BadgePublicTierSchema,
    category: BadgePublicCategorySchema,
    pointsReward: z.number().nonnegative(),
    isSecret: z.boolean(),
    earned: z.boolean(),
    earnedAt: z.string().optional(),
});
export const RankPublicSchema = z.object({
    code: z.string(),
    name: z.string(),
    thresholdPoints: z.number().nonnegative(),
    color: z.string(),
    icon: z.string(),
    perks: z.array(z.string()),
});
/**
 * Single source of truth for the points-ledger event-type enum.
 * Lives in `@evi/shared` because both `@evi/db` (Mongoose schema +
 * IPointsLedgerEntry.event_type) and the API/PWA boundary
 * (Zod-validated history responses) need access; dependency
 * direction in this codebase is `db → shared`, so the enum must
 * live here.
 *
 * Consumers:
 *   - `packages/db/src/models/PointsLedger.ts`: imports the
 *     `PointsEventType` TS type and pulls `.options` for the
 *     Mongoose `enum: [...]` constraint at runtime.
 *   - Zod-validated history APIs: parse against
 *     `PointsHistoryEntrySchema` which references this enum.
 *   - `apps/web/src/pages/rewards/RewardsPage.tsx`: typecheck-time
 *     exhaustiveness via `Record<PointsHistoryEntry['eventType'],
 *     string>` on EVENT_LABEL.
 *   - `packages/gamification/src/engine.ts`: re-exports as
 *     `EarnEventType` for the earn-input contract; ultimately
 *     resolves to this enum.
 *
 * Adding a new event type: add the literal here, ship. The Mongoose
 * enum picks it up automatically via `.options`, the rewards-page
 * exhaustive Record fails typecheck until EVENT_LABEL covers the
 * new value, and any new emit site is type-checked against
 * `EarnEventType`. No drift surface.
 */
export const PointsEventTypeSchema = z.enum([
    'SESSION_COMPLETED',
    // Session-completion flat bonus — separate row so the rewards
    // page can render it as its own line below the kWh-driven earn.
    // Both share the same `reference_id` (the session) so an operator
    // audit can group them.
    'SESSION_BONUS',
    'BADGE_EARNED',
    'REFERRAL_BONUS',
    // Friends Stage 2 — tier-based referrer milestone awards.
    // Distinct from REFERRAL_BONUS (legacy single-shot inviter +
    // referee welcome) so the rewards page renders tier hits as
    // their own line items. Multiple rows per (referrer, referee)
    // pair, capped by referralMaxMilestonesPerFriend; idempotency_key
    // per (referrer, referee, trigger_value) prevents duplicates.
    'REFERRAL_MILESTONE',
    'DAILY_STREAK',
    'MANUAL_ADJUSTMENT',
    'REDEMPTION',
    'MIGRATION_CARRYOVER',
    // Phase 4 — awarded on successful streak window continuation.
    'STREAK_CONTINUATION',
    // Phase 5 — awarded on weekly challenge completion.
    'CHALLENGE_COMPLETED',
    // Phase 6 — system debit when stale total_points expire. Negative
    // delta; lifetime_points is preserved.
    'EXPIRATION',
    // Phase 6 — system debit when a driver buys a skin / unlock.
    'UNLOCK_PURCHASE',
    // Phase 3 of gamification rules update — fixed +5 awarded when
    // a driver taps the Google review CTA on SessionCompletePage.
    // Trust-on-tap (no review-count delta verification). Idempotency
    // via the established `idempotency_key: 'session_review:<id>'`
    // pattern; one award per session via the existing partial unique
    // index on (tenant_id, idempotency_key).
    'REVIEW_AWARDED',
    // Gifting — a driver gifts points to a confirmed friend. Two rows
    // per gift: the giver's negative GIFT_SENT debit and the receiver's
    // positive GIFT_RECEIVED credit. The credit is written with the
    // engine's skipLifetimePoints flag so it raises total_points only —
    // gifts never inflate the receiver's lifetime_points or rank, and
    // the giver's rank is likewise untouched (negative deltas never
    // reduce lifetime). The counterparty rides reference_id
    // (reference_type 'gift_to' / 'gift_from') so the per-recipient
    // monthly cap can be summed from the giver's GIFT_SENT rows.
    'GIFT_SENT',
    'GIFT_RECEIVED',
    // Terms epic — one-time reward for turning on marketing consent
    // (express opt-in). Awarded once per user forever via the
    // idempotency key `marketing_opt_in:<user_id>`, so toggling the
    // preference off and on again never re-earns. Kept separate from
    // the ToS/terms acceptance flow — consent is optional and this
    // reward is the incentive, never implied by accepting the ToS.
    'MARKETING_OPT_IN',
    // Birthday reward — a once-per-calendar-year bonus a driver claims on/after
    // their birthday (month + day; no year stored). Fixed, tier-multiplier-exempt.
    // Awarded at most once per user per year via the idempotency key
    // `birthday:<user_id>:<year>`, layered under an atomic guard on the user's
    // birthday_claimed_year so a double-tap can never double-credit.
    'BIRTHDAY_BONUS',
]);
/** One-time reward (points) for turning on marketing consent. Fixed,
 *  tier-multiplier-exempt; awarded once per user via the stable
 *  idempotency key. */
export const MARKETING_OPT_IN_POINTS = 100;
/**
 * Birthday reward amount (points), granted once per calendar year on/after the
 * driver's birthday. Fixed, tier-multiplier-exempt. Set by Warren.
 */
export const BIRTHDAY_BONUS_POINTS = 500;
/** Times the birthday celebration can be REPLAYED after the claim (to share it). */
export const BIRTHDAY_CELEBRATION_MAX_REPLAYS = 3;
/** Days after the claim during which replays are allowed. */
export const BIRTHDAY_CELEBRATION_REPLAY_WINDOW_DAYS = 7;
/**
 * Cooldown window between location-scoped review awards. The same
 * driver can only earn the +5 review bonus once per location per
 * this many days.
 *
 * Implementation note: enforced via a fixed-window idempotency key
 * (`location_review:<location_id>:<user_id>:<window_id>` where
 * windowId = floor(epochMs / cooldownDaysMs)). Race-safe via the
 * existing partial unique index on points_ledger
 * (tenant_id, idempotency_key) — no separate cooldown collection or
 * transaction needed. Trade-off accepted at spec time: a driver
 * tapping near a window boundary could earn twice within 1-2 days
 * (last day of window N, first day of window N+1). Worst-case abuse
 * is 2x per cycle. Still bounded.
 *
 * Distinct from the session-scoped review (Phase 3) which uses
 * `session_review:<session_id>` as its key — once-per-session
 * forever. Both share the REVIEW_AWARDED event_type but distinguish
 * via reference_type ('session_review' vs 'location_review').
 */
export const LOCATION_REVIEW_COOLDOWN_DAYS = 60;
export const PointsHistoryEntrySchema = z.object({
    id: z.string(),
    eventType: PointsEventTypeSchema,
    pointsDelta: z.number(),
    balanceAfter: z.number(),
    note: z.string().optional(),
    createdAt: z.string(),
});
export const UserGamificationSummarySchema = z.object({
    totalPoints: z.number().nonnegative(),
    lifetimePoints: z.number().nonnegative(),
    currentRank: RankPublicSchema,
    nextRank: RankPublicSchema.nullable(),
    /** False when the ranks collection is unseeded: currentRank is then a
     *  hardcoded fallback and nextRank null — without this flag the client
     *  cannot tell that state from "at the top rank" and would show the
     *  max-rank congratulations to a 0-point user. Defaults true so older
     *  cached responses keep parsing. */
    ranksAvailable: z.boolean().default(true),
    /** 0-100 progress toward the next rank's threshold. */
    progressToNextPct: z.number().min(0).max(100),
    pointsToNext: z.number().nonnegative(),
    /** Every badge the caller has unlocked (includes earned secret badges). */
    earnedBadges: z.array(BadgePublicSchema),
    /** Last-20 ledger entries, newest first. Empty when the ledger is empty. */
    recentHistory: z.array(PointsHistoryEntrySchema),
    stats: z.object({
        sessionsCompleted: z.number().nonnegative(),
        energyKwhTotal: z.number().nonnegative(),
        uniqueStations: z.number().nonnegative(),
        referrals: z.number().nonnegative(),
        streakDays: z.number().nonnegative(),
        /** Live weekly charging streak (consecutive ISO weeks with a
         *  legitimate session) — the show-off streak badge reads this.
         *  Distinct from streakDays, the daily streak. */
        streakWeeks: z.number().nonnegative(),
    }),
});
//# sourceMappingURL=gamification.js.map